// App Footer - PWA Install & Share links
import React, { useState, useEffect } from 'react';
import { Download, Share2, Smartphone } from 'lucide-react';

interface AppFooterProps {
  className?: string;
}

export const AppFooter: React.FC<AppFooterProps> = ({ className = '' }) => {
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as any).standalone === true;
    setIsInstalled(isStandalone);

    // Detect iOS
    const ua = window.navigator.userAgent.toLowerCase();
    setIsIOS(/iphone|ipad|ipod/.test(ua) && !(window as any).MSStream);

    // Listen for install prompt
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSModal(true);
      return;
    }
    if (installPrompt) {
      await installPrompt.prompt();
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: 'Ring & Ash - Cigar Companion',
      text: 'Your personal cigar advisor, journal, and lounge finder.',
      url: 'https://ringandash.com',
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      // User cancelled or error
    }
  };

  return (
    <>
      <footer className={`border-t border-white/10 bg-stone-900/95 backdrop-blur-sm ${className}`}>
        <div className="flex items-center justify-center gap-4 px-4 py-3">
          {isInstalled ? (
            <div className="flex items-center gap-2 text-green-400 text-xs">
              <Smartphone className="w-3.5 h-3.5" />
              <span>Installed</span>
            </div>
          ) : (
            <button
              onClick={handleInstall}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/20 text-amber-500 hover:bg-amber-500/30 transition-colors text-xs font-medium"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Install App</span>
            </button>
          )}

          <div className="w-px h-4 bg-white/20" />

          <button
            onClick={handleShare}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-white/70 hover:bg-white/20 hover:text-white transition-colors text-xs font-medium"
          >
            <Share2 className="w-3.5 h-3.5" />
            <span>{copied ? 'Copied!' : 'Share'}</span>
          </button>
        </div>
      </footer>

      {/* iOS Instructions Modal */}
      {showIOSModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowIOSModal(false)} />
          <div className="relative bg-stone-800 rounded-2xl p-6 max-w-sm w-full border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-4">Install on iOS</h3>
            <div className="space-y-3 text-white/70 text-sm">
              <p>1. Tap the <strong className="text-white">Share</strong> button in Safari</p>
              <p>2. Scroll down and tap <strong className="text-white">"Add to Home Screen"</strong></p>
              <p>3. Tap <strong className="text-white">"Add"</strong> to install</p>
            </div>
            <button
              onClick={() => setShowIOSModal(false)}
              className="w-full mt-6 py-2.5 bg-amber-500 text-stone-900 font-medium rounded-xl hover:bg-amber-400"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AppFooter;
